/**
 * The pipeline page — `02/ Inquiries/ Table` (12:774) and `02/ Inquiries/ Board` (12:3742),
 * which are one screen with a view toggle over one URL-held query state.
 *
 * ===========================================================================
 * "+ SAVE VIEW" IS NOT BUILT
 * ===========================================================================
 * `PageHeader/Actions` (12:868) has "+ Save view" beside "+ New Inquiry". There is no saved
 * view anywhere in this schema — no table, no endpoint, no permission — so the button would
 * have to invent server state. It is omitted. Worth noting that the feature is not entirely
 * absent in practice: because every filter, the sort, the page size and the view itself live
 * in `?…` (see `query-state.ts`), a browser bookmark IS a saved view, and a pasted link
 * reopens somebody else's exact screen.
 *
 * ===========================================================================
 * WHAT THE PAGE OWNS (WEB TIER A · WT5 — LESS THAN IT DID)
 * ===========================================================================
 * Selection and the bulk outcome notice. The overlays this paragraph used to
 * list are GONE with the slide-over kill: the Peek is the record page at full
 * width (`inquiries/:inquiryId` — the row itself navigates), New Inquiry is
 * the create page (`inquiries/new`), and the follow-up is a routed page under
 * the record. Everything else stays delegated: the query state to
 * `useInquiryQuery`, the stage-move handshake to `useStageMoveController`
 * (which owns the optimistic edit, the rollback, the toast and the Undo).
 *
 * Selection is CLEARED whenever the result set changes. A selection that survived a filter
 * change would let a bulk action apply to rows the operator can no longer see — which the
 * API would happily perform, and which is exactly the kind of "I didn't do that" the audit
 * trail would then blame on them.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert } from '../../components/ui';
import type { InquiryStage } from '../../domain/stages';
import { InquiryToolbar } from './Toolbar';
import { InquiryTable } from './InquiryTable';
import { InquiryBoard } from './InquiryBoard';
import { BulkBar, BulkResultNotice } from './BulkBar';
import type { BulkOutcome } from './BulkBar';
import { LostReasonDialog } from './LostReasonDialog';
import { InquiryEmptyState, InquirySkeletonTable } from './states';
import { sortPatch, sortRows } from './sorting';
import type { SortColumn } from './sorting';
import { listParamsFor, toSearchParams, useInquiryQuery } from './query-state';
import type { InquiryQueryState } from './query-state';
import { useBulkUpdate, useInquiryList, useStageCounts, useStageMoveController } from './use-inquiries';
import { useColleagues, useDebouncedTerm } from '../directory/use-directory';
import { useToast } from './toast';
import type { MoveSource } from './stage-move';

export function InquiriesListPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, me } = useSession();
  const { state, update, search } = useInquiryQuery();
  const toast = useToast();
  const navigate = useNavigate();

  const canView = can(countryCode, 'inquiries.view');
  const canCreate = can(countryCode, 'inquiries.create');
  const canEdit = can(countryCode, 'inquiries.edit');
  const canStage = can(countryCode, 'inquiries.stage');
  // `GET /users` is `@RequirePerm('users.manage')` and `can()` already returns true for
  // `grp:true`, which mirrors the API's own group-admin bypass. Nothing else gates the
  // colleague picker (C5) and no permission grant changes to make it appear.
  const canListUsers = can(countryCode, 'users.manage');
  const myUserId = me === null ? null : String(me.id);

  const counts = useStageCounts(countryCode, canView);
  const list = useInquiryList(countryCode, state, canView && state.view === 'table');
  const mover = useStageMoveController(countryCode);
  const bulk = useBulkUpdate(countryCode);
  // `null` for a caller without the permission — the Assign menu then renders exactly as it
  // did before the picker existed. `myUserId` is excluded because "Assign to me" is already
  // in that menu.
  // The picker searches now (ESC-DIRECTORY): the raw term drives the input so typing
  // never lags, and the debounced copy drives the request so a five-letter name is one
  // query rather than five.
  const [colleagueTerm, setColleagueTerm] = useState('');
  const colleagues = useColleagues(
    countryCode,
    canListUsers,
    myUserId,
    useDebouncedTerm(colleagueTerm),
  );

  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);

  /**
   * The identity of the current result set. When it changes, the selection is stale.
   * Serialised rather than compared field by field so adding a filter later cannot forget
   * to clear.
   *
   * Cleared with React's "adjust state when a prop changes" pattern rather than an effect:
   * an effect would let one render commit with a stale selection against fresh rows, which
   * is the exact window in which a bulk action could hit a row the operator can no longer
   * see.
   */
  const listIdentity = useMemo(() => JSON.stringify(listParamsFor(state)), [state]);
  const [selectionScope, setSelectionScope] = useState(listIdentity);
  if (selectionScope !== listIdentity) {
    setSelectionScope(listIdentity);
    if (selected.size > 0) setSelected(new Set());
  }

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Inquiries are not available to this account">
          Viewing the pipeline in {countryCode} needs the Inquiries permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const sorted = sortRows(rows, state.sort, state.dir);
  const filtered = state.q.trim() !== '' || state.assigned !== null;

  const hrefFor = (patch: Partial<InquiryQueryState>): string => {
    const params = toSearchParams({ ...state, ...patch, page: 1 });
    const qs = params.toString();
    return qs === '' ? `/countries/${countryCode}/inquiries` : `/countries/${countryCode}/inquiries?${qs}`;
  };

  const recordHref = (id: string): string =>
    `/countries/${countryCode}/inquiries/${id}${search}`;
  // WT5 — the panel openers became routes: the create page, and the follow-up
  // page under the record (which keeps the list context in `?…` the same way
  // the record link does, so Cancel/Save land back where the operator was).
  const createHref = `/countries/${countryCode}/inquiries/new`;
  const followupHref = (id: string): string =>
    `/countries/${countryCode}/inquiries/${id}/follow-up${search}`;

  // `InquiryDto` is structurally a `MoveSource`, so the table, the board and the follow-up
  // panel all reach the controller through this one funnel.
  const requestMove = (row: MoveSource, target: string, reason?: string): void => {
    mover.requestMove(
      { id: row.id, doc_no: row.doc_no, stage: row.stage, lost_reason: row.lost_reason },
      target,
      reason,
    );
  };

  const runBulk = (action: 'assign' | 'stage', value: string | null): void => {
    const ids = [...selected];
    if (ids.length === 0) return;
    bulk.mutate(
      { ids, action, value },
      {
        onSuccess: (result) => {
          setSelected(new Set());
          setOutcome(result);
          // A clean run gets a toast; a partial one deliberately does NOT — see
          // `BulkResultNotice`. A success toast over refused rows is a false green, and so
          // is one over `warnings[]`: every id succeeded there, but the work is now parked
          // where its assignee cannot see it. Both go to the persistent panel instead.
          if (result.refused.length === 0 && (result.warnings ?? []).length === 0) {
            toast.push({
              text: `${String(result.updated.length)} of ${String(result.requested)} inquiries updated`,
            });
          }
        },
        onError: () => {
          toast.push({
            tone: 'error',
            text: 'The bulk action was refused. Nothing was changed.',
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Inquiries"
        subtitle={
          counts.data === undefined
            ? undefined
            : `${String(counts.data.total)} ${counts.data.total === 1 ? 'inquiry' : 'inquiries'}`
        }
        actions={
          canCreate ? (
            /* WT5 — a ROUTE, so it is a real anchor (middle-click, copy link).
             * The classes are `Button`'s primary variant verbatim; a `Button`
             * with an onClick-navigate would hide the URL it goes to. */
            <Link
              to={createHref}
              className="inline-flex h-9 select-none items-center justify-center gap-2 rounded-sm bg-indigo-strong px-4 text-body font-medium text-fg-inverse transition-colors hover:bg-indigo-strong-hover active:bg-indigo-strong-active"
            >
              <Plus aria-hidden="true" className="size-4" />
              New Inquiry
            </Link>
          ) : undefined
        }
      />

      <InquiryToolbar
        state={state}
        counts={counts.data?.counts ?? null}
        total={counts.data?.total ?? null}
        myUserId={myUserId}
        onPatch={update}
      />

      {outcome === null ? null : (
        <BulkResultNotice outcome={outcome} onDismiss={() => setOutcome(null)} />
      )}

      {state.view === 'board' ? (
        <InquiryBoard
          countryCode={countryCode}
          state={state}
          // WT5 — a card opens the RECORD now (the peek at full width).
          onOpenRecord={(id) => navigate(recordHref(id))}
          onMove={(row, target) => requestMove(row, target)}
          canStage={canStage}
          canCreate={canCreate}
          // The board's in-column "+ New inquiry" tile goes where the header
          // action goes — same gate, same destination, one route.
          onNewInquiry={() => navigate(createHref)}
          tableHrefForStage={(stage) => hrefFor({ view: 'table', stage })}
        />
      ) : list.isPending ? (
        <InquirySkeletonTable />
      ) : list.isError ? (
        <Alert tone="danger" title="The inquiry list could not be loaded">
          The stage counts above may still be accurate. Reload to try again.
        </Alert>
      ) : rows.length === 0 ? (
        <InquiryEmptyState
          countryCode={countryCode}
          stage={state.stage}
          filtered={filtered}
          counts={counts.data?.counts ?? null}
          canCreate={canCreate}
          onClearFilters={() => update({ q: '', assigned: null })}
          newInquiryHref={createHref}
          hrefForStage={(stage: InquiryStage) => hrefFor({ view: 'table', stage })}
        />
      ) : (
        <InquiryTable
          rows={sorted}
          countryCode={countryCode}
          total={total}
          state={state}
          selected={selected}
          onSelect={(id, next) =>
            setSelected((current) => {
              const copy = new Set(current);
              if (next) copy.add(id);
              else copy.delete(id);
              return copy;
            })
          }
          onSelectPage={(next) =>
            setSelected(next ? new Set(rows.map((row) => row.id)) : new Set())
          }
          onSort={(column: SortColumn) =>
            update(sortPatch({ sort: state.sort, dir: state.dir }, column))
          }
          onPage={(page) => update({ page })}
          onPerPage={(perPage) => update({ perPage })}
          recordHref={recordHref}
          onMoveStage={(row, stage) => requestMove(row, stage)}
          onAddFollowup={(row) => navigate(followupHref(row.id))}
          canStage={canStage}
          canEdit={canEdit}
        />
      )}

      <BulkBar
        count={selected.size}
        busy={bulk.isPending}
        canAssign={canEdit}
        countryCode={countryCode}
        myUserId={myUserId}
        colleagues={colleagues}
        colleagueTerm={colleagueTerm}
        onColleagueTermChange={setColleagueTerm}
        onClear={() => setSelected(new Set())}
        onMoveToStage={(stage) => runBulk('stage', stage)}
        onAssign={(userId) => runBulk('assign', userId)}
      />

      {/* WT5 — the three slide-overs that closed this file are DELETED, not
        * relocated: the Peek is `inquiries/:inquiryId` (the row navigates),
        * New Inquiry is `inquiries/new`, the follow-up is
        * `inquiries/:inquiryId/follow-up`. `LostReasonDialog` survives — it is
        * a centered dialog serving the stage-move handshake, not a slide-over. */}
      <LostReasonDialog
        move={mover.awaitingReason}
        busy={mover.isMoving}
        onSubmit={mover.submitReason}
        onCancel={mover.cancelReason}
      />
    </div>
  );
}
